import type { MouseEvent } from 'react';
import { useState, useId } from 'react';

export interface UsePopoverOptions {
  /** Custom ID for the popover element (auto-generated if not provided) */
  id?: string;
}

/**
 * Manages popover state with anchor element and ARIA-compliant ID.
 *
 * Generic hook for any popover/menu pattern. Returns handlers and state
 * needed for MUI Popover/Menu components.
 *
 * Automatically generates unique IDs using React's useId, or accepts
 * a custom ID for more descriptive semantics.
 *
 * @example
 * ```tsx
 * // Auto-generated unique ID
 * const { anchorEl, open, id, handleOpen, handleClose } = usePopover();
 *
 * // Custom ID for better semantics
 * const settingsPopover = usePopover({ id: 'settings-menu' });
 *
 * <IconButton onClick={handleOpen} aria-describedby={id}>
 *   <SettingsIcon />
 * </IconButton>
 *
 * <Popover
 *   id={id}
 *   open={open}
 *   anchorEl={anchorEl}
 *   onClose={handleClose}
 * >
 *   {/* content *\/}
 * </Popover>
 * ```
 */
export const usePopover = (options: UsePopoverOptions = {}) => {
  const generatedId = useId();
  const baseId = options.id ?? `popover-${generatedId}`;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? baseId : undefined;

  return { anchorEl, open, id, handleOpen, handleClose };
};
